export const TCGPLAYER_AFFILIATE_BASE_URL =
  "https://partner.tcgplayer.com/c/6433560/1780961/21018";

const TCGPLAYER_PARTNER_HOST = "partner.tcgplayer.com";
const TCGPLAYER_MASS_ENTRY_PRODUCT_LINE = "Flesh & Blood TCG";
const TCGPLAYER_MASS_ENTRY_BASE_URL = "https://www.tcgplayer.com/massentry";
const DEFAULT_MAX_AFFILIATE_URL_LENGTH = 256_000;

export interface TcgPlayerAffiliateUrlOptions {
  subId1?: string;
}

export interface TcgPlayerMassEntryLineInput {
  quantity?: number | string | null;
  name?: string | null;
  setCode?: string | null;
  cardNumber?: string | number | null;
  isPhysical?: boolean | null;
}

export interface TcgPlayerMassEntryLine {
  quantity: number;
  name: string;
  setCode?: string;
  cardNumber?: string;
  text: string;
}

export interface TcgPlayerMassEntryList {
  lines: TcgPlayerMassEntryLine[];
  listText: string;
  totalQuantity: number;
  skippedCount: number;
}

export interface TcgPlayerMassEntryUrlOptions
  extends TcgPlayerAffiliateUrlOptions {
  maxAffiliateUrlLength?: number;
}

export interface TcgPlayerMassEntryUrlResult {
  url: string;
  plainUrl: string;
  prefilledUrl: string;
  listText: string;
  isPrefilled: boolean;
  lineCount: number;
  totalQuantity: number;
  skippedCount: number;
}

function parseUrl(value: string): URL | undefined {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
}

function isTcgPlayerHost(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return (
    normalized === "tcgplayer.com" || normalized.endsWith(".tcgplayer.com")
  );
}

function isTcgPlayerPartnerUrl(url: URL): boolean {
  return url.hostname.toLowerCase() === TCGPLAYER_PARTNER_HOST;
}

function appendSubId1(url: string, subId1?: string): string {
  const normalizedSubId = String(subId1 ?? "").trim();
  if (!normalizedSubId) return url;
  if (/[?&]subId1=/i.test(url)) return url;

  const hashIndex = url.indexOf("#");
  const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
  const separator = base.includes("?")
    ? base.endsWith("?") || base.endsWith("&")
      ? ""
      : "&"
    : "?";

  return `${base}${separator}subId1=${encodeURIComponent(normalizedSubId)}${hash}`;
}

function normalizeWhitespace(value: unknown): string {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeSetCode(value: unknown): string {
  return normalizeWhitespace(value).replace(/^\[/, "").replace(/\]$/, "");
}

function normalizeMassEntryQuantity(value: unknown): number {
  const quantity = Math.floor(Number(value));
  if (!Number.isFinite(quantity) || quantity <= 0) return 0;
  return quantity;
}

function buildMassEntryLineText(line: {
  quantity: number;
  name: string;
  setCode?: string;
  cardNumber?: string;
}): string {
  const setCode = normalizeSetCode(line.setCode);
  const cardNumber = normalizeWhitespace(line.cardNumber);
  return [
    String(line.quantity),
    line.name,
    setCode ? `[${setCode}]` : "",
    cardNumber,
  ]
    .filter(Boolean)
    .join(" ");
}

function buildMassEntryLineKey(line: {
  name: string;
  setCode?: string;
  cardNumber?: string;
}): string {
  return [
    normalizeWhitespace(line.name).toLowerCase(),
    normalizeSetCode(line.setCode).toLowerCase(),
    normalizeWhitespace(line.cardNumber).toLowerCase(),
  ].join("|");
}

function buildMassEntryUrl(listText?: string): string {
  const productLine = encodeURIComponent(TCGPLAYER_MASS_ENTRY_PRODUCT_LINE);
  const base = `${TCGPLAYER_MASS_ENTRY_BASE_URL}?productline=${productLine}`;
  const normalizedList = String(listText ?? "").trim();
  if (!normalizedList) return base;

  const encodedLines = normalizedList
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(encodeURIComponent);

  return encodedLines.length ? `${base}&c=${encodedLines.join("||")}` : base;
}

export function buildTcgPlayerAffiliateUrl(
  urlInput: string | null | undefined,
  options: TcgPlayerAffiliateUrlOptions = {},
): string {
  const url = String(urlInput ?? "").trim();
  if (!url) return "";

  const parsed = parseUrl(url);
  if (!parsed) return url;
  if (!isTcgPlayerHost(parsed.hostname)) return url;
  if (isTcgPlayerPartnerUrl(parsed)) return url;

  const destinationUrl = appendSubId1(url, options.subId1);
  return `${TCGPLAYER_AFFILIATE_BASE_URL}?u=${encodeURIComponent(destinationUrl)}`;
}

export function buildTcgPlayerProductUrl(
  productIdInput: string | number | null | undefined,
  options: TcgPlayerAffiliateUrlOptions = {},
): string | undefined {
  const productId = String(productIdInput ?? "").trim();
  if (!/^\d+$/.test(productId)) return undefined;

  return buildTcgPlayerAffiliateUrl(
    `https://www.tcgplayer.com/product/${encodeURIComponent(productId)}`,
    options,
  );
}

export function buildTcgPlayerMassEntryList(
  linesInput: TcgPlayerMassEntryLineInput[],
): TcgPlayerMassEntryList {
  const linesByKey = new Map<string, TcgPlayerMassEntryLine>();
  let skippedCount = 0;

  for (const input of linesInput) {
    if (input?.isPhysical === false) {
      skippedCount += 1;
      continue;
    }

    const quantity = normalizeMassEntryQuantity(input?.quantity);
    const name = normalizeWhitespace(input?.name);
    const setCode = normalizeSetCode(input?.setCode);
    const cardNumber = normalizeWhitespace(input?.cardNumber);
    if (!quantity || !name) {
      skippedCount += 1;
      continue;
    }

    const key = buildMassEntryLineKey({ name, setCode, cardNumber });
    const existing = linesByKey.get(key);
    const nextQuantity = (existing?.quantity ?? 0) + quantity;
    const line = {
      quantity: nextQuantity,
      name,
      setCode: setCode || undefined,
      cardNumber: cardNumber || undefined,
      text: buildMassEntryLineText({
        quantity: nextQuantity,
        name,
        setCode,
        cardNumber,
      }),
    };
    linesByKey.set(key, line);
  }

  const lines = Array.from(linesByKey.values()).sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: "base" }),
  );
  const listText = lines.map((line) => line.text).join("\n");
  const totalQuantity = lines.reduce((sum, line) => sum + line.quantity, 0);

  return { lines, listText, totalQuantity, skippedCount };
}

export function buildTcgPlayerMassEntryUrl(
  input: {
    lines: TcgPlayerMassEntryLineInput[];
  },
  options: TcgPlayerMassEntryUrlOptions = {},
): TcgPlayerMassEntryUrlResult {
  const list = buildTcgPlayerMassEntryList(input.lines);
  const prefilledUrl = buildMassEntryUrl(list.listText);
  const plainUrl = buildMassEntryUrl();
  const affiliatePrefilledUrl = buildTcgPlayerAffiliateUrl(prefilledUrl, {
    subId1: options.subId1,
  });
  const maxLength =
    options.maxAffiliateUrlLength ?? DEFAULT_MAX_AFFILIATE_URL_LENGTH;
  const isPrefilled =
    list.lines.length > 0 && affiliatePrefilledUrl.length <= maxLength;
  const url = isPrefilled
    ? affiliatePrefilledUrl
    : buildTcgPlayerAffiliateUrl(plainUrl, { subId1: options.subId1 });

  return {
    url,
    plainUrl,
    prefilledUrl,
    listText: list.listText,
    isPrefilled,
    lineCount: list.lines.length,
    totalQuantity: list.totalQuantity,
    skippedCount: list.skippedCount,
  };
}
